import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaPaperclip } from 'react-icons/fa';
import ImageUpload, { ImageUploadHandle } from '@/components/ImageUpload/ImageUpload';
import HintTooltip from '@/components/HintTooltip';
import api from '@/utils/api';
import { buildSignBroadcast } from '@/utils/kleverTx';
import { useAppSelector } from '@/redux/Store';
import './PostComposer.scss';

export type PostPreviewMode = 'none' | 'char_limit' | 'text_only';

export interface PostSubmitData {
  body: string;
  mediaUrl?: string;
  locationId?: string;
  viewType?: 'public' | 'paid' | 'ppv';
  ppvPrice?: string;
  ppvTokenId?: string;
  postId?: string; // pre-reserved UUID for PPV
  /** PPV preview mode (#257). Server defaults to 'char_limit' if
   *  unset on create; on a public/paid post this is ignored. */
  previewMode?: PostPreviewMode;
  /** Only consulted when previewMode === 'char_limit'. Server clamps
   *  to (0, 1000]; defaults to 200 if unset or invalid. */
  previewCharLimit?: number;
  /** Creator-declared AI generation flag (#531). Composer surfaces a
   *  checkbox; the value drives a subtle badge on the post card. */
  isAiGenerated?: boolean;
}

interface PostComposerProps {
  onSubmit: (data: PostSubmitData) => Promise<void>;
  /** Creator's on-chain profileId — required to show PPV option */
  profileId?: number;
  placeholder?: string;
  maxLength?: number;
  /** Edit mode (#292): pre-fills fields and disables view-type / PPV
   *  price picking (those live on-chain and can't be re-registered).
   *  body / mediaUrl / preview mode / preview char limit / AI flag
   *  stay editable. */
  initial?: {
    body?: string;
    mediaUrl?: string;
    viewType?: 'public' | 'paid' | 'ppv';
    ppvPrice?: string | null;
    previewMode?: PostPreviewMode;
    previewCharLimit?: number | null;
    isAiGenerated?: boolean;
  };
  /** Label for the submit button. Defaults to "Post" (create), pass
   *  "Save changes" for edit. */
  submitLabel?: string;
}


interface TagSuggestion {
  type: 'hashtag';
  label: string;   // "#klyx"
  value: string;   // "klyx"
  count: number;
}

interface UserSuggestion {
  type: 'mention';
  label: string;       // "@alice"  (username, shown as sub-label)
  value: string;       // what gets inserted after @
  avatar?: string;
  displayName?: string;
}

type Suggestion = TagSuggestion | UserSuggestion;

interface ActiveToken {
  type: '#' | '@';
  query: string;
  start: number;   // index in body where the token (#/@) begins
  end: number;     // cursor position (exclusive end)
}

/** Walk backward from cursorPos to find a #word or @word token being typed. */
function getActiveToken(value: string, cursorPos: number): ActiveToken | null {
  let i = cursorPos - 1;
  while (i >= 0 && !/[\s\n]/.test(value[i])) i--;
  const token = value.slice(i + 1, cursorPos);
  if (token.startsWith('#') && token.length > 1) {
    return { type: '#', query: token.slice(1), start: i + 1, end: cursorPos };
  }
  if (token.startsWith('@') && token.length > 1) {
    return { type: '@', query: token.slice(1), start: i + 1, end: cursorPos };
  }
  return null;
}

/**
 * Escape HTML entities and wrap #hashtag / @mention tokens in highlight marks.
 * Used for the syntax-highlight backdrop layer.
 */
function getHighlightHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped
    .replace(/#([a-zA-Z][a-zA-Z0-9_]{0,49})/g, '<mark class="hl-hashtag">#$1</mark>')
    .replace(/@([a-zA-Z0-9_]{1,50})/g, '<mark class="hl-mention">@$1</mark>');
}

/** Strip whitespace and keep only characters valid in a mention token. */
function sanitizeInsertName(raw: string): string {
  return raw.replace(/\s+/g, '').replace(/[^a-zA-Z0-9_]/g, '');
}

const DEBOUNCE_MS = 200;

const PostComposer: React.FC<PostComposerProps> = ({
  onSubmit,
  profileId,
  placeholder = "What's on your mind?",
  maxLength = 500,
  initial,
  submitLabel = 'Post',
}) => {
  const authUser = useAppSelector((state) => state.auth.user);
  const isEmailUser = authUser?.authMethod === 'email';
  // Edit mode when the parent hands us existing values (#292). The
  // on-chain-immutable fields (view_type, ppvPrice) get pre-filled
  // AND locked; the editable ones just pre-fill.
  const isEditing = initial !== undefined;

  const [body, setBody]                         = useState(initial?.body ?? '');
  const [mediaUrl, setMediaUrl]                 = useState(initial?.mediaUrl ?? '');
  const [isSubmitting, setIsSubmitting]         = useState(false);
  // Image upload is now triggered directly from a paperclip icon
  // (no two-click "show uploader, then click 'Choose Image'" dance).
  // The uploader still renders inline once a file is picked so the
  // user can preview / remove. uploaderRef.openPicker() opens the
  // native file dialog from outside the component.
  const uploaderRef = useRef<ImageUploadHandle | null>(null);
  const [error, setError]                       = useState<string | null>(null);
  const [viewType, setViewType]                 = useState<'public' | 'paid' | 'ppv'>(initial?.viewType ?? 'public');
  const [ppvPrice, setPpvPrice]                 = useState(initial?.ppvPrice ?? '');
  const [ppvStep, setPpvStep]                   = useState<'idle' | 'registering' | 'done'>('idle');
  // PPV preview controls (#257). char_limit matches the server
  // default; 200 chars is the same default the server applies if
  // we don't send a number.
  const [previewMode, setPreviewMode]           = useState<PostPreviewMode>(initial?.previewMode ?? 'char_limit');
  const [previewCharLimit, setPreviewCharLimit] = useState<number>(initial?.previewCharLimit ?? 200);
  // AI generation flag (#531) — off by default; creator opts in.
  const [isAiGenerated, setIsAiGenerated]       = useState<boolean>(initial?.isAiGenerated ?? false);

  // Autocomplete state
  const [suggestions, setSuggestions]           = useState<Suggestion[]>([]);
  const [activeToken, setActiveToken]           = useState<ActiveToken | null>(null);
  const [selectedIndex, setSelectedIndex]       = useState(0);

  const textareaRef   = useRef<HTMLTextAreaElement>(null);
  const backdropRef   = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track an uploaded-but-unsubmitted post image so we can clean it up if
  // the user navigates away without submitting. Cleared on successful submit.
  const orphanedUploadRef = useRef<string | null>(null);

  // ── Auto-grow textarea ───────────────────────────────────────────────────

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  // ── Clean up abandoned image uploads ────────────────────────────────────
  // If the user uploads an image and then leaves without submitting, delete
  // the file from the server so it doesn't linger forever.
  useEffect(() => {
    return () => {
      const orphan = orphanedUploadRef.current;
      if (!orphan) return;
      api.delete('/upload/post-image', { data: { url: orphan } }).catch(() => {
        // Best-effort cleanup — ignore failures (file may already be deleted)
      });
    };
  }, []);

  // ── Sync backdrop scroll with textarea scroll ────────────────────────────

  const syncScroll = () => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // ── Autocomplete fetch ──────────────────────────────────────────────────

  const fetchSuggestions = useCallback(async (token: ActiveToken) => {
    if (token.query.length === 0) { setSuggestions([]); return; }

    try {
      if (token.type === '#') {
        const { data } = await api.get('/search/tags', { params: { q: token.query, limit: 6 } });
        setSuggestions(
          (data.results ?? []).map((r: any): TagSuggestion => ({
            type:  'hashtag',
            label: r.label,
            value: r.tag,
            count: r.post_count,
          })),
        );
      } else {
        const { data } = await api.get('/search/users', { params: { q: token.query, limit: 6 } });
        setSuggestions(
          (data.results ?? []).map((r: any): UserSuggestion => {
            // Prefer displayName (r.title) stripped of spaces; fall back to username (r.subtitle)
            const rawName   = r.title ?? r.subtitle;
            const sanitized = sanitizeInsertName(rawName);
            const insertAs  = sanitized.length >= 1 && /^[a-zA-Z0-9_]{1,50}$/.test(sanitized)
              ? sanitized
              : (r.subtitle ?? r.title);
            return {
              type:        'mention',
              label:       `@${r.subtitle ?? r.title}`,
              value:       insertAs,
              avatar:      r.avatar,
              displayName: r.title,
            };
          }),
        );
      }
      setSelectedIndex(0);
    } catch {
      setSuggestions([]);
    }
  }, []);

  // ── Textarea change handler ─────────────────────────────────────────────

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue  = e.target.value;
    const cursor    = e.target.selectionStart ?? newValue.length;
    setBody(newValue);

    const token = getActiveToken(newValue, cursor);
    setActiveToken(token);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (token) {
      debounceTimer.current = setTimeout(() => fetchSuggestions(token), DEBOUNCE_MS);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelect = useCallback((i: number) => {
    if (!activeToken || !suggestions[i]) return;
    const s = suggestions[i];
    const insert = s.type === 'hashtag' ? `#${s.value} ` : `@${s.value} `;
    const newBody =
      body.slice(0, activeToken.start) + insert + body.slice(activeToken.end);
    setBody(newBody);
    setSuggestions([]);
    setActiveToken(null);

    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      const pos = activeToken.start + insert.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }, [activeToken, body, suggestions]);

  // ── Keyboard nav in dropdown ────────────────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleSelect(selectedIndex);
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActiveToken(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (textareaRef.current && !textareaRef.current.closest('.post-composer')?.contains(e.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() || isSubmitting) return;
    if (viewType === 'ppv' && (!ppvPrice || parseFloat(ppvPrice) <= 0)) {
      setError('Enter a valid PPV price greater than 0');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      let reservedPostId: string | undefined;

      // Edit mode (#292): the on-chain PPV registration is immutable
      // — never re-run the register-ppv tx path from an edit form.
      // Fields we accept from edits are body / mediaUrl / preview
      // mode / preview char limit / isAiGenerated. Everything else
      // is either untouched or blocked by the disabled inputs.
      if (viewType === 'ppv' && !isEditing) {
        if (!profileId) throw new Error('Profile ID required for PPV posts');

        // 1. Reserve UUID
        setPpvStep('registering');
        const { data: prepData } = await api.post('/posts/prepare');
        reservedPostId = prepData.postId;

        if (isEmailUser) {
          // Email users: backend signs + broadcasts using stored private key
          await api.post('/posts/tx/register-ppv-email', {
            profileId,
            postId: reservedPostId,
            price: ppvPrice,
          });
        } else {
          // Wallet users: get unsigned tx, sign with Klever extension
          const { data: txData } = await api.post('/posts/tx/register-ppv', {
            profileId,
            postId: reservedPostId,
            price: ppvPrice,
          });

          await buildSignBroadcast(txData.tx);
        }

        setPpvStep('done');
      }

      await onSubmit({
        body: body.trim(),
        mediaUrl: mediaUrl.trim() || undefined,
        viewType,
        ppvPrice: viewType === 'ppv' ? ppvPrice : undefined,
        postId: reservedPostId,
        previewMode: viewType === 'ppv' ? previewMode : undefined,
        previewCharLimit:
          viewType === 'ppv' && previewMode === 'char_limit' ? previewCharLimit : undefined,
        isAiGenerated: isAiGenerated || undefined,
      });

      // Image is now attached to a published post — no longer an orphan
      orphanedUploadRef.current = null;

      // Reset only on create — edit keeps its pre-fills in the form
      // so if the parent modal doesn't unmount immediately after the
      // save the form stays populated with the just-saved values
      // (natural preview of what the card will look like).
      if (isEditing) return;

      setBody('');
      setMediaUrl('');
      setSuggestions([]);
      setViewType('public');
      setPpvPrice('');
      setPpvStep('idle');
      setPreviewMode('char_limit');
      setPreviewCharLimit(200);

      toast.success(viewType === 'ppv' ? 'PPV post published!' : 'Post published!');
    } catch (err: any) {
      setError(err.message || 'Failed to create post');
      toast.error(err.message || 'Failed to create post');
      setPpvStep('idle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remainingChars = maxLength - body.length;
  const isOverLimit    = remainingChars < 0;

  return (
    <div className="post-composer">
      <form onSubmit={handleSubmit}>
        <div className="composer-header">
          <h3>Create Post</h3>
        </div>

        <div className="composer-input-wrap">
          {/* Syntax-highlight backdrop (sits behind the transparent textarea) */}
          <div
            ref={backdropRef}
            className="composer-highlight-backdrop"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: getHighlightHtml(body) + ' ' }}
          />

          <textarea
            ref={textareaRef}
            id='composer-textarea-post'
            className="composer-textarea"
            placeholder={placeholder}
            value={body}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onScroll={syncScroll}
            rows={4}
            disabled={isSubmitting}
          />

          {/* Autocomplete dropdown */}
          {suggestions.length > 0 && (
            <ul className="composer-suggestions" role="listbox">
              {suggestions.map((s, i) => (
                <li
                  key={s.value}
                  role="option"
                  aria-selected={i === selectedIndex}
                  className={`suggestion-item${i === selectedIndex ? ' selected' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(i); }}
                >
                  {s.type === 'mention' ? (
                    <>
                      {s.avatar
                        ? <img src={s.avatar} alt="" className="suggestion-avatar" />
                        : <span className="suggestion-avatar suggestion-avatar--placeholder">
                            {(s.displayName ?? s.value)[0].toUpperCase()}
                          </span>
                      }
                      <span className="suggestion-name">{s.displayName ?? s.value}</span>
                      <span className="suggestion-sub">{s.label}</span>
                    </>
                  ) : (
                    <>
                      <span className="suggestion-hash">#</span>
                      <span className="suggestion-name">{s.value}</span>
                      <span className="suggestion-sub">{s.count.toLocaleString()} posts</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Meta row directly under the textarea: hashtag/mention hint
            on the left, remaining-character count on the right. Moved
            here from the bottom toolbar so the hint sits next to the
            input it's actually about. */}
        <div className="composer-meta-row">
          <span className="composer-hint"># hashtag &nbsp;@ mention</span>
          <span className={`char-count ${isOverLimit ? 'over-limit' : ''}`}>
            {remainingChars} characters remaining
          </span>
        </div>

        {/* ImageUpload is always mounted now so the uploaderRef
            stays stable across renders. The component renders nothing
            in the empty state (hideDefaultTrigger=true); once a file
            is picked it shows the preview + remove button. */}
        <div className="image-upload-container">
          <ImageUpload
            ref={uploaderRef}
            onUploadComplete={(url) => {
              setMediaUrl(url);
              orphanedUploadRef.current = url;
            }}
            currentImage={mediaUrl}
            endpoint="post-image"
            fieldName="image"
            maxSizeMB={10}
            hideDefaultTrigger
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* View type selector — disabled in edit mode (#292) because
            view_type is stamped at create time (PPV posts hit the
            on-chain register-ppv path, public/paid persist to a
            column that later reads gate access). Editing view_type
            after publish would break access invariants for existing
            purchasers. Same reasoning gates ppvPrice below. */}
        <div className="composer-visibility">
          <button
            type="button"
            className={`visibility-btn ${viewType === 'public' ? 'active' : ''}`}
            onClick={() => setViewType('public')}
            disabled={isEditing}
            title={isEditing ? 'Post type is fixed after publish' : 'Visible to everyone'}
          >
            🌐 Public
          </button>
          <button
            type="button"
            className={`visibility-btn ${viewType === 'paid' ? 'active' : ''}`}
            onClick={() => setViewType('paid')}
            disabled={isEditing}
            title={isEditing ? 'Post type is fixed after publish' : 'Requires view fee or subscription'}
          >
            🔓 Subscribe
          </button>
          {profileId && (
            <span className="visibility-btn-wrap">
              <button
                type="button"
                className={`visibility-btn ${viewType === 'ppv' ? 'active' : ''}`}
                onClick={() => setViewType('ppv')}
                disabled={isEditing}
                title={isEditing ? 'Post type is fixed after publish' : undefined}
              >
                🔑 PPV
              </button>
              <HintTooltip
                text="Pay-Per-View: each viewer pays once to permanently unlock this post."
                placement="top"
              />
            </span>
          )}
        </div>

        {/* PPV price input */}
        {viewType === 'ppv' && (
          <div className="composer-ppv">
            <label htmlFor="ppv-price" className="ppv-label">
              PPV Price (KLV)
              <HintTooltip
                text="Each user pays this price once to permanently unlock this post. You receive 95% of every payment."
                placement="right"
              />
            </label>
            <input
              id="ppv-price"
              type="number"
              min="0.01"
              step="0.01"
              className="ppv-price-input"
              placeholder="e.g. 5.0"
              value={ppvPrice}
              onChange={(e) => {
                const val = e.target.value;
                // Clamp to 2 decimal places
                const match = val.match(/^\d*\.?\d{0,2}/);
                setPpvPrice(match ? match[0] : '');
              }}
              disabled={isSubmitting || isEditing}
            />
            <span className="ppv-note">⚠️ Price is permanent once set on-chain</span>

            {/* Preview mode picker (#257). Lets the creator choose what
                unauthenticated / unpaid viewers see before they pay. */}
            <div className="ppv-preview-picker">
              <span className="ppv-preview-label">
                Preview for unpaid viewers
                <HintTooltip
                  text="What people see before they pay. 'Char limit' is best for text posts; 'Text only' is best for posts where the image is the paywalled value; 'None' is a hard paywall."
                  placement="right"
                />
              </span>
              <div className="ppv-preview-options">
                <button
                  type="button"
                  className={`ppv-preview-btn ${previewMode === 'char_limit' ? 'active' : ''}`}
                  onClick={() => setPreviewMode('char_limit')}
                  disabled={isSubmitting}
                >
                  Char limit
                </button>
                <button
                  type="button"
                  className={`ppv-preview-btn ${previewMode === 'text_only' ? 'active' : ''}`}
                  onClick={() => setPreviewMode('text_only')}
                  disabled={isSubmitting}
                >
                  Text only (no image)
                </button>
                <button
                  type="button"
                  className={`ppv-preview-btn ${previewMode === 'none' ? 'active' : ''}`}
                  onClick={() => setPreviewMode('none')}
                  disabled={isSubmitting}
                >
                  None
                </button>
              </div>
              {previewMode === 'char_limit' && (
                <div className="ppv-preview-charlimit">
                  <label htmlFor="ppv-preview-chars">
                    Visible characters
                  </label>
                  <input
                    id="ppv-preview-chars"
                    type="number"
                    min={20}
                    max={1000}
                    step={10}
                    value={previewCharLimit}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      if (Number.isFinite(n)) {
                        setPreviewCharLimit(Math.max(20, Math.min(1000, n)));
                      }
                    }}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI-generated flag (#531). Creator-declared — off by default,
            opt-in. Rendered above the footer so it sits with the other
            post-shaping controls rather than the action row. */}
        <div className="composer-ai-toggle">
          <label className="composer-ai-toggle__label">
            <input
              type="checkbox"
              checked={isAiGenerated}
              onChange={(e) => setIsAiGenerated(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>This post is AI-generated</span>
          </label>
          <HintTooltip
            text="Turn this on if the post's text or media was generated by an AI tool. It shows a small badge on the post so viewers know."
            placement="right"
          />
        </div>

        <div className="composer-footer">
          <div className="composer-actions">
            {/* Paperclip opens the native file picker directly — used
                to be a 🖼️ button that revealed an "Add image" panel
                with its own "Choose Image" button (two clicks). The
                uploader is always mounted just behind this trigger. */}
            <button
              type="button"
              className="btn-icon-action"
              onClick={() => uploaderRef.current?.openPicker()}
              disabled={isSubmitting}
              title="Attach image"
              aria-label="Attach image"
            >
              <FaPaperclip />
            </button>
          </div>

          <div className="composer-submit">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!body.trim() || isOverLimit || isSubmitting}
            >
              {isSubmitting
                ? ppvStep === 'registering' ? 'Registering on-chain…'
                : ppvStep === 'done' ? 'Saving post…'
                : isEditing ? 'Saving…'
                : 'Posting…'
                : submitLabel}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PostComposer;
